import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchReturnTickets,
  updateReturnTicket,
} from "../../../redux/slices/adminReturnSlice";

const statuses = [
  "Requested",
  "Under Review",
  "Approved",
  "Item Received",
  "Refund Processing",
  "Refunded",
  "Rejected",
  "Cancelled",
];

const refundMethods = [
  "Original Payment Method",
  "Bank Transfer",
  "Store Credit",
  "Other",
];

export default function AdminReturnsPage() {
  const dispatch = useDispatch();
  const { tickets, loading, error } = useSelector((s) => s.adminReturns);

  const [expandedId, setExpandedId] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    dispatch(fetchReturnTickets());
  }, [dispatch]);

  const handleUpdate = (id) => {
    const data = editData[id] || {};
    dispatch(updateReturnTicket({ id, ...data }));
    setExpandedId(null);
  };

  const updateField = (id, field, value) => {
    setEditData({
      ...editData,
      [id]: {
        ...editData[id],
        [field]: value,
      },
    });
  };

  if (loading && !tickets.length) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <p className="text-gray-600">Loading return tickets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Return Requests</h1>
          <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-semibold">
            {tickets.length} Total
          </span>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {!tickets || tickets.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <p className="text-gray-500">No return requests found.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {tickets.map((ticket) => {
              const isExpanded = expandedId === ticket._id;
              const data = editData[ticket._id] || {};

              return (
                <div
                  key={ticket._id}
                  className="bg-white rounded-lg shadow-md overflow-hidden"
                >
                  {/* Header */}
                  <div className="p-6 border-b">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold">
                            Order #{ticket.order?._id || ticket.order}
                          </h3>
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-semibold ${
                              ticket.status === "Refunded"
                                ? "bg-green-100 text-green-700"
                                : ticket.status === "Rejected"
                                ? "bg-red-100 text-red-700"
                                : ticket.status === "Approved"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            {ticket.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Customer:</span>
                            <span className="ml-2 font-medium">
                              {ticket.name}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Email:</span>
                            <span className="ml-2 font-medium">
                              {ticket.email}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Phone:</span>
                            <span className="ml-2 font-medium">
                              {ticket.phone}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Reason:</span>
                            <span className="ml-2 font-medium">
                              {ticket.reason}
                            </span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-gray-500">Submitted:</span>
                            <span className="ml-2 font-medium">
                              {new Date(ticket.createdAt).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setExpandedId(isExpanded ? null : ticket._id)
                        }
                        className="ml-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition"
                      >
                        {isExpanded ? "Collapse" : "Manage"}
                      </button>
                    </div>

                    {ticket.message && (
                      <div className="mt-4 p-3 bg-gray-50 rounded">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">Message:</span>{" "}
                          {ticket.message}
                        </p>
                      </div>
                    )}

                    {ticket.images?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-semibold mb-2">
                          Attached Images:
                        </p>
                        <div className="flex gap-2">
                          {ticket.images.map((img, i) => (
                            <a
                              key={i}
                              href={img.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <img
                                src={img.url}
                                alt={`Return evidence ${i + 1}`}
                                className="w-20 h-20 object-cover rounded border hover:opacity-80 transition"
                              />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Expanded Management Section */}
                  {isExpanded && (
                    <div className="p-6 bg-gray-50 space-y-4">
                      {/* Status Update */}
                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          Update Status
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {statuses.map((status) => (
                            <button
                              key={status}
                              onClick={() =>
                                updateField(ticket._id, "status", status)
                              }
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                (data.status || ticket.status) === status
                                  ? "bg-blue-600 text-white"
                                  : "bg-white border hover:bg-gray-50"
                              }`}
                            >
                              {status}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Admin Note */}
                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          Admin Note
                        </label>
                        <textarea
                          placeholder="Add internal notes or message to customer..."
                          value={data.adminNote ?? ticket.adminNote ?? ""}
                          onChange={(e) =>
                            updateField(ticket._id, "adminNote", e.target.value)
                          }
                          className="w-full border rounded-lg p-3 text-sm"
                          rows={3}
                        />
                      </div>

                      {/* Return Tracking */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold mb-2">
                            Return Tracking Number
                          </label>
                          <input
                            type="text"
                            placeholder="e.g., 1234567890"
                            value={
                              data.returnTrackingNumber ??
                              ticket.returnTrackingNumber ??
                              ""
                            }
                            onChange={(e) =>
                              updateField(
                                ticket._id,
                                "returnTrackingNumber",
                                e.target.value
                              )
                            }
                            className="w-full border rounded-lg p-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold mb-2">
                            Return Tracking Link
                          </label>
                          <input
                            type="url"
                            placeholder="https://..."
                            value={
                              data.returnTrackingLink ??
                              ticket.returnTrackingLink ??
                              ""
                            }
                            onChange={(e) =>
                              updateField(
                                ticket._id,
                                "returnTrackingLink",
                                e.target.value
                              )
                            }
                            className="w-full border rounded-lg p-2 text-sm"
                          />
                        </div>
                      </div>

                      {/* Refund Information */}
                      <div className="border-t pt-4">
                        <h4 className="text-sm font-bold mb-3">
                          Refund Information
                        </h4>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-semibold mb-2">
                              Refund Amount
                            </label>
                            <input
                              type="number"
                              placeholder="0.00"
                              step="0.01"
                              value={
                                data.refundAmount ?? ticket.refundAmount ?? ""
                              }
                              onChange={(e) =>
                                updateField(
                                  ticket._id,
                                  "refundAmount",
                                  e.target.value
                                )
                              }
                              className="w-full border rounded-lg p-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2">
                              Refund Method
                            </label>
                            <select
                              value={
                                data.refundMethod ?? ticket.refundMethod ?? ""
                              }
                              onChange={(e) =>
                                updateField(
                                  ticket._id,
                                  "refundMethod",
                                  e.target.value
                                )
                              }
                              className="w-full border rounded-lg p-2 text-sm"
                            >
                              <option value="">Select method</option>
                              {refundMethods.map((method) => (
                                <option key={method} value={method}>
                                  {method}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-2">
                              Transaction ID
                            </label>
                            <input
                              type="text"
                              placeholder="Optional"
                              value={
                                data.refundTransactionId ??
                                ticket.refundTransactionId ??
                                ""
                              }
                              onChange={(e) =>
                                updateField(
                                  ticket._id,
                                  "refundTransactionId",
                                  e.target.value
                                )
                              }
                              className="w-full border rounded-lg p-2 text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3 pt-4">
                        <button
                          onClick={() => handleUpdate(ticket._id)}
                          disabled={loading}
                          className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50"
                        >
                          {loading ? "Saving..." : "Save Changes"}
                        </button>
                        <button
                          onClick={() => {
                            setExpandedId(null);
                            setEditData({ ...editData, [ticket._id]: {} });
                          }}
                          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
